import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkRect, SkCircle, SkEllipse, SkLine } from "../../prim";
import { skSun, skTree } from "../../actors3";
import { skGrassTuft } from "../../actors3/nature";
import { skCat, skDog } from "../../actors3/animals";
import { skArrow } from "../../actors3/objects";
import type { SkinSceneProps } from "./SkinParkScene";

// ─── SkinPhotosynthesisScene ──────────────────────────────────────────────────
/**
 * A plant drinks water, the sun sends light photons, the leaves produce oxygen —
 * all looping in ~6 s. Teaches: "Plants make food from sunlight!".
 */
export const SkinPhotosynthesisScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "Plants make food from sunlight!",
}) => {
  const skin = useSkin();
  const ground = 330;
  const LOOP = 6; // loop period in seconds

  // ── Photons: 5 yellow circles travel from sun (140,70) toward tree canopy (340,ground-80)
  const photons = Array.from({ length: 5 }, (_, i) => {
    const phase = (t / LOOP + i / 5) % 1; // 0→1 within each loop
    const px = 140 + (340 - 140) * phase;
    const py = 70 + (ground - 80 - 70) * phase;
    const op = phase < 0.85 ? 1 - phase * 0.4 : 1 - (phase - 0.85) / 0.15;
    return { px, py, op };
  });

  // ── Water droplets: 4 blue circles rise from soil (340,ground) up through roots to canopy
  const drops = Array.from({ length: 4 }, (_, i) => {
    const phase = (t / LOOP + i / 4) % 1;
    const dy = ground + 20 - (phase * (ground + 20 - (ground - 120)));
    const op = phase < 0.8 ? 0.85 : 0.85 * (1 - (phase - 0.8) / 0.2);
    return { dy, op };
  });

  // ── Oxygen bubbles: 4 green circles drift from canopy upward and slightly right
  const bubbles = Array.from({ length: 4 }, (_, i) => {
    const phase = (t / LOOP + i / 4) % 1;
    const bx = 340 + 20 + phase * 60 + Math.sin(phase * Math.PI * 3 + i) * 18;
    const by = (ground - 80) - phase * 120;
    const op = phase < 0.75 ? 0.9 : 0.9 * (1 - (phase - 0.75) / 0.25);
    return { bx, by, op };
  });

  return (
    <g opacity={opacity}>
      {/* sky background */}
      <rect width={800} height={450} fill={skin.surface} />
      {/* ground */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill="#a3c96e" seed={2000} />
      {/* soil band under ground */}
      <SkRect x={0} y={ground + 30} w={800} h={450 - ground - 30} fill="#92400e" seed={2001} />

      {/* sun */}
      {skSun(140, 70, t, 1.1)}

      {/* tree / plant */}
      {skTree(340, ground, t, 1.4)}

      {/* grass tufts */}
      {skGrassTuft(80, ground, t, 1)}
      {skGrassTuft(220, ground, t, 0.9)}
      {skGrassTuft(500, ground, t, 1.05)}
      {skGrassTuft(650, ground, t, 0.85)}

      {/* photon rays label area: sun beam line hint */}
      <SkLine x1={165} y1={88} x2={300} y2={230} stroke="#fbbf24" strokeWidth={1.5} opacity={0.25} seed={2010} />

      {/* photons — yellow light circles */}
      {photons.map(({ px, py, op }, i) => (
        <SkCircle key={i} cx={px} cy={py} r={7} fill="#fde047" seed={2020 + i} opacity={op} />
      ))}

      {/* root trunk visual (dark brown rectangle below tree base) */}
      <SkRect x={333} y={ground} w={14} h={40} fill="#78350f" seed={2030} />

      {/* water droplets — blue, rising up the trunk */}
      {drops.map(({ dy, op }, i) => (
        <SkCircle key={i} cx={340 + (i % 2 === 0 ? -5 : 5)} cy={dy} r={5} fill="#38bdf8" seed={2040 + i} opacity={op} />
      ))}

      {/* oxygen bubbles — green, floating out from canopy */}
      {bubbles.map(({ bx, by, op }, i) => (
        <SkCircle key={i} cx={bx} cy={by} r={6 + i} fill="none" stroke="#4ade80" strokeWidth={2} seed={2050 + i} opacity={op} />
      ))}

      {/* legend labels (small, inside scene) */}
      <g transform="translate(160 118)">
        <rect x={-22} y={-12} width={44} height={18} rx={9} fill="rgba(253,224,71,0.88)" />
        <text x={0} y={3} textAnchor="middle" fill="#78350f" fontSize={10} fontWeight={700} fontFamily={skin.fontFamily}>light</text>
      </g>
      <g transform="translate(336 355)">
        <rect x={-22} y={-12} width={44} height={18} rx={9} fill="rgba(56,189,248,0.88)" />
        <text x={0} y={3} textAnchor="middle" fill="#0c4a6e" fontSize={10} fontWeight={700} fontFamily={skin.fontFamily}>water</text>
      </g>
      <g transform="translate(420 210)">
        <rect x={-24} y={-12} width={48} height={18} rx={9} fill="rgba(74,222,128,0.88)" />
        <text x={0} y={3} textAnchor="middle" fill="#166534" fontSize={10} fontWeight={700} fontFamily={skin.fontFamily}>oxygen</text>
      </g>

      {/* caption */}
      <g transform="translate(400 38)">
        <rect x={-255} y={-22} width={510} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>{label}</text>
      </g>
    </g>
  );
};

// ─── SkinFoodChainScene ───────────────────────────────────────────────────────
/**
 * Grass → Rabbit (cat stand-in) → Dog, with pulsing energy arrows between them.
 * The cat nibbles the grass then the dog bounces toward the cat — cute / non-scary.
 * Loop ~8 s. Teaches: "Who eats who!".
 */
export const SkinFoodChainScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "Who eats who!",
}) => {
  const skin = useSkin();
  const ground = 330;
  const LOOP = 8;
  const phase = (t % LOOP) / LOOP; // 0→1 within each loop

  // ── Cat (rabbit) nibble: head bobs down toward grass (0→0.4) then straightens
  const nibblePhase = phase < 0.4 ? phase / 0.4 : 0;
  const catHeadDip = Math.sin(nibblePhase * Math.PI) * 18; // dip down then back

  // ── Dog approach: sits at right (x=620) during 0→0.5, trots toward cat (x=460) 0.5→0.8, retreats 0.8→1
  const dogBaseX = 620;
  const catX = 220;
  const dogTargetX = catX + 180;
  let dogX: number;
  if (phase < 0.5) {
    dogX = dogBaseX;
  } else if (phase < 0.8) {
    const p = (phase - 0.5) / 0.3;
    dogX = dogBaseX + (dogTargetX - dogBaseX) * p;
  } else {
    const p = (phase - 0.8) / 0.2;
    dogX = dogTargetX + (dogBaseX - dogTargetX) * p;
  }

  // ── Arrow 1 pulse: between grass cluster and cat
  const arrow1x = 130; // midpoint between grass (x≈80) and cat (x≈220)

  // ── Arrow 2 pulse: between cat and dog
  const arrow2x = 420; // midpoint between cat (x≈220) and dog starting position

  return (
    <g opacity={opacity}>
      {/* sky */}
      <rect width={800} height={450} fill={skin.surface} />
      {/* ground */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill="#86efac" seed={2100} />

      {/* ── Grass cluster (left) — multiple tufts */}
      {skGrassTuft(55, ground, t, 1.2)}
      {skGrassTuft(85, ground, t, 1.0)}
      {skGrassTuft(115, ground, t, 1.1)}
      {/* extra lush green patch under grass */}
      <SkEllipse cx={85} cy={ground - 4} rx={52} ry={14} fill="#4ade80" seed={2110} opacity={0.6} />

      {/* ── Energy arrow 1: grass → cat */}
      {skArrow(arrow1x, ground - 40, t, 0.85, "right")}

      {/* ── Cat (rabbit stand-in) nibbling grass */}
      <g transform={`translate(0 ${catHeadDip})`}>
        {skCat(catX, ground, t, 1.1, "#f5a142")}
      </g>

      {/* ── Energy arrow 2: cat → dog */}
      {skArrow(arrow2x, ground - 40, t, 0.85, "right")}

      {/* ── Dog trotting right-to-left */}
      {skDog(dogX, ground, t, 1.15, "#d97706")}

      {/* food-chain role labels */}
      <g transform="translate(85 295)">
        <rect x={-30} y={-13} width={60} height={20} rx={10} fill="rgba(255,255,255,0.82)" stroke="#a7f3d0" strokeWidth={1.2} />
        <text x={0} y={3} textAnchor="middle" fill={skin.textInk} fontSize={11} fontWeight={700} fontFamily={skin.fontFamily}>Plant</text>
      </g>
      <g transform="translate(220 292)">
        <rect x={-34} y={-13} width={68} height={20} rx={10} fill="rgba(255,255,255,0.82)" stroke="#fde68a" strokeWidth={1.2} />
        <text x={0} y={3} textAnchor="middle" fill={skin.textInk} fontSize={11} fontWeight={700} fontFamily={skin.fontFamily}>Herbivore</text>
      </g>
      <g transform="translate(620 292)">
        <rect x={-34} y={-13} width={68} height={20} rx={10} fill="rgba(255,255,255,0.82)" stroke="#fca5a5" strokeWidth={1.2} />
        <text x={0} y={3} textAnchor="middle" fill={skin.textInk} fontSize={11} fontWeight={700} fontFamily={skin.fontFamily}>Carnivore</text>
      </g>

      {/* caption */}
      <g transform="translate(400 38)">
        <rect x={-180} y={-22} width={360} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>{label}</text>
      </g>
    </g>
  );
};
